// Right-hand panel of the CDI section: per-region affected-area bars, the
// national "area exposed" table, and a short explanation of the split.

import { CDI_CLASSES, NO_DROUGHT_COLOR } from "../reportConfig";

const CLASS_KEYS = CDI_CLASSES.map((c) => c.key);

function formatKm2(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return Math.round(value).toLocaleString("en-US");
}

// Total drought-affected area for a region. Prefer the backend's `affected_area`
// but fall back to summing the class areas so the bars still colour correctly
// against an API that predates that field.
function affectedAreaOf(row) {
  const explicit = Number(row.affected_area);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return CLASS_KEYS.reduce((sum, key) => sum + (Number(row[key]) || 0), 0);
}

function RegionBar({ row, maxAffected }) {
  const affected = affectedAreaOf(row);
  // Prefer the true "% of the region affected"; otherwise size the bar relative
  // to the worst-affected region so the ranking still reads.
  const hasPct = row.percentage_affected != null;
  const fill = hasPct
    ? Math.min(row.percentage_affected, 100)
    : maxAffected > 0
      ? Math.min((affected / maxAffected) * 100, 100)
      : 0;

  const denom = CLASS_KEYS.reduce((sum, key) => sum + (Number(row[key]) || 0), 0) || 1;
  const segments = CDI_CLASSES.map((cls) => ({
    ...cls,
    // Each class as its share of the filled portion of the bar.
    width: (fill * (Number(row[cls.key]) || 0)) / denom,
  }));

  return (
    <li className="region-bar">
      <div className="region-bar__head">
        <span className="region-bar__name">{row.admin_one_name}</span>
        <span className="region-bar__value">
          {hasPct ? `${Math.round(row.percentage_affected)}%` : formatKm2(affected)}
        </span>
      </div>
      <div className="region-bar__track">
        {segments.map((seg) => (
          <span
            key={seg.key}
            className="region-bar__seg"
            style={{ width: `${seg.width}%`, backgroundColor: seg.color }}
            title={`${seg.label}: ${formatKm2(row[seg.key])} km²`}
          />
        ))}
        <span
          className="region-bar__seg region-bar__seg--rest"
          style={{
            width: `${Math.max(0, 100 - fill)}%`,
            backgroundColor: NO_DROUGHT_COLOR,
          }}
        />
      </div>
    </li>
  );
}

/**
 * @param {object} props
 * @param {object|null} props.cdiOverview The `cdi` payload from `data=cdi_overview`.
 * @param {string} props.explanation Short analyst/AI note about the class split.
 * @param {boolean} [props.loading] Show skeletons while the overview loads.
 */
export default function CdiOverviewPanel({ cdiOverview, explanation, loading = false }) {
  const overview = cdiOverview?.overview || [];
  const totals = cdiOverview?.total_area_per_per_class || {};
  const pct = cdiOverview?.percentage_area || {};
  const maxAffected = overview.reduce((m, r) => Math.max(m, affectedAreaOf(r)), 0);
  const pending = loading && !cdiOverview;

  return (
    <div className="cdi-panel">
      <div className="cdi-panel__bar">Administration level one overview</div>

      <p className="cdi-panel__subhead">CDI affected area by admin region</p>
      {pending ? (
        <ul className="region-bars">
          {[0, 1, 2, 3, 4].map((i) => (
            <li key={i} className="region-bar region-bar--skeleton">
              <span className="region-bar__track" />
            </li>
          ))}
        </ul>
      ) : overview.length ? (
        <ul className="region-bars">
          {overview.slice(0, 8).map((row) => (
            <RegionBar key={row.admin_one_name} row={row} maxAffected={maxAffected} />
          ))}
        </ul>
      ) : (
        <p className="cdi-panel__empty">No significant observed drought at admin-1 level.</p>
      )}

      <div className="cdi-panel__bar">Area exposed km²</div>
      <table className="area-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Area km²</th>
            <th>% Area</th>
          </tr>
        </thead>
        <tbody>
          {CDI_CLASSES.map((cls) => (
            <tr key={cls.key}>
              <td>
                <span className="area-table__dot" style={{ backgroundColor: cls.color }} />
                {cls.label}
              </td>
              <td>{formatKm2(totals[cls.key])}</td>
              <td>{pct[cls.key] != null ? `${pct[cls.key].toFixed(1)}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="cdi-panel__note">
        <p className="cdi-panel__note-title">CDI situation analysis</p>
        {pending ? (
          <p className="cdi-panel__loading">
            <span className="report-spinner" /> Preparing the analysis…
          </p>
        ) : (
          <p>{explanation}</p>
        )}
      </div>
    </div>
  );
}
